"use client"

import { useEffect, useState } from "react"
import { useQuery, useMutation } from "@tanstack/react-query"
import {
  Pencil,
  User,
  Copy,
  Edit,
  Trash2,
  Plus,
  Upload,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  Star,
  Building,
  Users,
  Briefcase,
  Twitter,
  Instagram,
  Linkedin,
  Globe,
  MapPin,
  Facebook,
  X,
} from "lucide-react"
import Sidebar from "../startupdashboard/admin_sidebar"
import { submitFounderKyc, getFounderKyc, getAllFounderKycs, uploadImage, uploadFile } from "../utils/api"
import { getFounders, getFounderById, createFounder, updateFounder, deleteFounder, getAllStages, getAllIndustries } from "../utils/api"
import axios from "axios"
import toast from "react-hot-toast"
import { ClockLoader } from "react-spinners"
import ModernHeader from "./modern-header"
import ModernSidebar from "./admin_sidebar"
import { useDispatch, useSelector } from 'react-redux';
import { setLogoUrl, restoreLogoUrl } from '../utils/logoSlice';
import Modal from '../components/modal';
import { tamilNaduSubdistricts } from "../utils/tamilNaduSubdistricts";
import { isGuest } from "../utils/isGuest"
import { useNavigate } from "react-router-dom"


const tamilNaduDistricts = Object.keys(tamilNaduSubdistricts);

// Skeleton Loader Components
const ProfileSkeleton = () => (
  <div className="animate-pulse">
    <div className="bg-gradient-to-r from-slate-200 to-slate-300 rounded-2xl p-8 shadow-2xl h-48 mb-8"></div>

    <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
      <div className="xl:col-span-2 space-y-8">
        {/* Startup Profile Card Skeleton */}
        <div className="bg-white/80 rounded-2xl p-8 shadow-xl border border-white/50">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-slate-300 rounded-xl w-12 h-12"></div>
            <div className="bg-slate-300 rounded h-8 w-48"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="bg-slate-200 rounded h-4 w-24"></div>
                <div className="bg-slate-100 rounded-xl h-12 w-full"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Founder Details Card Skeleton */}
        <div className="bg-white/80 rounded-2xl p-8 shadow-xl border border-white/50">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-slate-300 rounded-xl w-12 h-12"></div>
              <div className="bg-slate-300 rounded h-8 w-32"></div>
            </div>
            <div className="bg-slate-300 rounded-xl h-10 w-32"></div>
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <div className="min-w-full">
              <div className="bg-gradient-to-r from-slate-100 to-slate-200 grid grid-cols-8 gap-4 p-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="bg-slate-300 rounded h-4"></div>
                ))}
              </div>
              <div className="bg-white divide-y divide-gray-200">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="grid grid-cols-8 gap-4 p-4">
                    {[...Array(8)].map((_, j) => (
                      <div key={j} className="bg-slate-100 rounded h-6"></div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Startup Details Card Skeleton */}
        <div className="bg-white/80 rounded-2xl p-8 shadow-xl border border-white/50">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-slate-300 rounded-xl w-12 h-12"></div>
            <div className="bg-slate-300 rounded h-8 w-48"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="bg-slate-200 rounded h-4 w-24"></div>
                <div className="bg-slate-100 rounded-xl h-12 w-full"></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Column Skeleton */}
      <div className="space-y-6">
        <div className="bg-white/80 rounded-2xl p-6 shadow-xl border border-white/50">
          <div className="bg-slate-300 rounded h-6 w-32 mb-4"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-slate-100 rounded-xl h-16 w-full"></div>
            ))}
          </div>
        </div>

        <div className="bg-white/80 rounded-2xl p-8 shadow-xl border border-white/50">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-slate-300 rounded-xl w-12 h-12"></div>
            <div className="bg-slate-300 rounded h-8 w-48"></div>
          </div>
          <div className="space-y-6">
            <div>
              <div className="bg-slate-200 rounded h-4 w-24 mb-2"></div>
              <div className="bg-slate-100 rounded-xl h-12 w-full"></div>
            </div>
            <div>
              <div className="bg-slate-200 rounded h-4 w-24 mb-2"></div>
              <div className="flex items-center gap-4">
                <div className="bg-slate-100 rounded-full w-24 h-24"></div>
                <div className="bg-slate-100 rounded-lg h-10 w-32"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AdminProfile = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const [kycModalOpen, setKycModalOpen] = useState(false)
  const [addFounderModalOpen, setAddFounderModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [kycStatus, setKycStatus] = useState(null)
  const [editingFounderId, setEditingFounderId] = useState(null)
  const [founders, setFounders] = useState([])
  const [currentFounderForKyc, setCurrentFounderForKyc] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [founderToDelete, setFounderToDelete] = useState(null)
  const [pitchDeckFile, setPitchDeckFile] = useState(null)
  const [email, setEmail] = useState("")
  const [fullName, setFullName] = useState("")
  const [logoFile, setLogoFile] = useState(null);
  const [pitchDeckUploading, setPitchDeckUploading] = useState(false);
  const [stages, setStages] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const dispatch = useDispatch();
  const logoUrl = useSelector((state) => state.logo.logoUrl);
  const navigate=useNavigate()

  useEffect(() => {
    dispatch(restoreLogoUrl());
  }, [dispatch]);

  const [founderForm, setFounderForm] = useState({
    name: "",
    email: "",
    phone: "",
  })

  const [formData, setFormData] = useState({
    _id: "",
    startupId: "",
    fullName: "",
    phone: "",
    email: "",
    linkedin: "",
    twitter: "",
    instagram: "",
    facebook: "",
    dpiitNumber: "",
    username: "",
    password: "",
    shortDescription: "",
    startupName: "",
    industry: "",
    stage: "",
    location: "",
    address: "",
    logoUrl: "",
    pitchDeck: "",
    support: [],
    status: "active",
    isRegistered: false,
    createdAt: "",
    updatedAt: "",
    __v: 0
  });

  const [kycData, setKycData] = useState({
    startupId: "",
    founderName: "",
    email: "",
    address: "",
    phoneNumber: "",
    aadharNumber: "",
    panNumber: "",
    DIN: "",
  })

  const startupId = localStorage.getItem("startupId")
  const toggleSidebar = () => setSidebarOpen((prev) => !prev)

  // Calculate profile completion percentage
  const calculateProgress = (data) => {
    const requiredFields = data.isRegistered
      ? ['startupName', 'email', 'phone', 'linkedin', 'location', 'logoUrl', 'industry', 'twitter', 'instagram', 'facebook', 'shortDescription', 'stage', 'address', 'pitchDeck']
      : ['startupName', 'email', 'phone', 'linkedin', 'location', 'logoUrl', 'industry', 'twitter', 'instagram', 'facebook', 'shortDescription', 'stage', 'address', 'pitchDeck'];

    const totalFields = requiredFields.length;
    let completedFields = 0;

    requiredFields.forEach(field => {
      // Check if field exists and is not empty
      if (data[field] && data[field].toString().trim() !== '') {
        completedFields++;
      }
    });

    return Math.round((completedFields / totalFields) * 100);
  };

  // Fetch profile data using React Query
  const { isLoading: isLoadingProfile } = useQuery({
    queryKey: ['startupProfile', startupId],
    queryFn: async () => {
      if(isGuest()){
        setFullName("Guest Mode")
        setEmail(localStorage.getItem("email"))
        setFormData({...formData,fullName:"Guest Mode",email:localStorage.getItem("email"),startupName:"Guest IT"})

      }
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/startups/${startupId}`)
      const data = {
        ...response.data,
        logo: response.data.logoUrl || response.data.logo
      };
      setFormData(data)
      setFullName(data.fullName)
      setEmail(data.email)
      setProgressPercentage(calculateProgress(data))
      return data
    },
    enabled: !!startupId,
    onError: (error) => {
      toast.error("Failed to load profile data")
      console.error("Error fetching profile:", error)
    }
  })

  // Fetch founders and KYC data using React Query
  const { isLoading: isLoadingFounders } = useQuery({
    queryKey: ['foundersAndKyc'],
    queryFn: async () => {
      const [foundersResp, kycResp] = await Promise.all([
        getFounders(),
        getAllFounderKycs()
      ]);

      const foundersData = Array.isArray(foundersResp?.data)
        ? foundersResp.data
        : Array.isArray(foundersResp?.data?.data)
          ? foundersResp.data.data
          : Array.isArray(foundersResp)
            ? foundersResp
            : [];

      const kycArray = Array.isArray(kycResp?.data)
        ? kycResp.data
        : Array.isArray(kycResp?.data?.data)
          ? kycResp.data.data
          : Array.isArray(kycResp)
            ? kycResp
            : [];

      // Create a map of KYC data by email for easy lookup
      const kycByEmail = {};
      kycArray.forEach(kyc => {
        kycByEmail[kyc.email] = kyc;
      });

      const merged = foundersData.map((f, idx) => {
        const kyc = kycByEmail[f.email];
        return {
          ...f,
          id: f._id,
          sno: idx + 1,
          founderId: kyc?.founderId ?? "Verify KYC to get Founder ID",
          status: kyc?.verification_status ?? "Not Submitted",
          DIN: kyc?.DIN ?? "-",
          founderName: kyc?.founderName ?? f.name,
          phone: kyc?.phoneNumber ?? f.phone
        };
      });

      setFounders(merged);
      return merged;
    },
    onError: (error) => {
      toast.error("Failed to load founders data")
      console.error("Error fetching founders:", error)
    }
  })

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (mobile) setSidebarOpen(false)
    }
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [industriesRes, stagesRes] = await Promise.all([
          getAllIndustries(),
          getAllStages()
        ]);

        const stagesData = Array.isArray(stagesRes) ? stagesRes : stagesRes?.data || [];
        const industriesData = Array.isArray(industriesRes) ? industriesRes : industriesRes?.data || [];

        setIndustries(industriesData);
        setStages(stagesData);

      } catch (error) {
        console.error("Error fetching dropdown data", error);
        setIndustries([]);
        setStages([]);
      }
    };

    fetchDropdownData();
  }, []);

  const getDisplayName = (name, email) => {
    if (name && typeof name === 'string') return name;
    if (!email) return "User";

    const username = email.split('@')[0];
    return username.charAt(0).toUpperCase() + username.slice(1);
  };

  const fetchKycStatus = async (founderId, startupId) => {
    try {
      if (founderId) {
        const data = await getFounderKyc(founderId)
        setKycStatus(data.verification_status)
      } else if (startupId) {
        const allKycs = await getAllFounderKycs()
        const match = allKycs.find((item) => item.startupId === startupId)
        if (match) {
          setKycStatus(match.verification_status)
          localStorage.setItem("founderId", match.founderId)
        }
      }
    } catch (err) {
      console.error("Error fetching KYC status:", err)
    }
  }

  const validatePhoneNumber = (phone) => {
    if (!phone) return true;

    const phoneRegex = /^[1-9]\d{0,9}$/;
    return phoneRegex.test(phone);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "phone") {
      const numericValue = value.replace(/\D/g, '');

      if (numericValue && !validatePhoneNumber(numericValue)) {
        toast.error("Invalid phone number");
        return;
      }

      const limitedValue = numericValue.slice(0, 10);

      setFormData((prev) => ({ ...prev, [name]: limitedValue }));
    } else if (name === "pitchDeck" && files && files[0]) {
      setPitchDeckFile(files[0]);
      setFormData((prev) => ({ ...prev, [name]: files[0].name }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Update progress percentage after change
    setTimeout(() => {
      setProgressPercentage(calculateProgress({ ...formData, [name]: value }));
    }, 100);
  };

  // Mutation for updating profile
  const updateProfileMutation = useMutation({
    mutationFn: async (payload) => {
      const { data } = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/startups/${formData._id}`,
        payload
      )
      return data
    },
    onSuccess: (data) => {
      setFormData(prev => ({
        ...prev,
        ...data.startup,
        logo: data.startup.logoUrl,
      }))
      setProgressPercentage(calculateProgress(data.startup))
      setEditMode(false)
      setLogoFile(null)
      setPitchDeckFile(null)
      if (logoFile && formData.logo && formData.logo.startsWith('blob:')) {
        URL.revokeObjectURL(formData.logo)
      }
    },
    onError: (err) => {
      console.error("Error updating profile:", err)
      toast.error(err.response?.data?.message || "Failed to update profile")
    }
  })

  const handleSave = async () => {
    setIsSubmitting(true);

    // Validate phone number format
    if (formData.phone && !validatePhoneNumber(formData.phone)) {
      toast.error("Please enter a valid 10-digit phone number that doesn't start with 0");
      setIsSubmitting(false);
      return;
    }

    // Validate required fields
    const requiredFields = formData.isRegistered
      ? ['startupName', 'email', 'phone', 'linkedin', 'location', 'logoUrl', 'industry']
      : ['startupName', 'email', 'phone', 'linkedin'];

    const missingFields = requiredFields.filter(field =>
      !formData[field] || formData[field].toString().trim() === ''
    );

    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`);
      setIsSubmitting(false);
      return;
    }

    try {
      let newLogoUrl = formData.logoUrl;
      let newPitchDeckUrl = formData.pitchDeck;

      // Upload new logo if needed
      if (logoFile) {
        const { url } = await uploadImage(logoFile);
        newLogoUrl = url;
        dispatch(setLogoUrl(url));
      }

      // Upload new pitch deck if needed
      if (pitchDeckFile) {
        const { url } = await uploadFile(pitchDeckFile);
        newPitchDeckUrl = url;
      }

      const payload = {
        ...formData,
        logoUrl: newLogoUrl,
        pitchDeck: newPitchDeckUrl,
      };

      await updateProfileMutation.mutateAsync(payload);

      toast.success("Profile updated successfully!");
      setEditMode(false);
      setLogoFile(null);
      setPitchDeckFile(null);
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKycChange = (e) => {
    const { name, value } = e.target
    setKycData((prev) => ({ ...prev, [name]: value }))
  }

  const openKycModal = () => {
    setKycData((prev) => ({
      ...prev,
      startupId: formData.startupId || "",
      founderName: formData.fullName,
      email: formData.email,
    }))
    setKycModalOpen(true)
  }

  const openKycModalForFounder = (founder) => {
    setCurrentFounderForKyc(founder)
    setKycData((prev) => ({
      ...prev,
      startupId: founder.startupId || "",
      founderName: founder.name,
      email: founder.email,
      phoneNumber: founder.phone || "",
      address: "",
      aadharNumber: "",
      panNumber: "",
      DIN: "",
    }))
    setKycModalOpen(true)
  }

  // Mutation for submitting KYC
  const submitKycMutation = useMutation({
    mutationFn: async (kycData) => {
      return await submitFounderKyc({
        ...kycData,
        founderId: currentFounderForKyc.id,
      })
    },
    onSuccess: (result) => {
      const founderId = result?.data?.founderId
      if (founderId) {
        setFounders(
          founders.map((f) =>
            f.id === currentFounderForKyc.id
              ? {
                ...f,
                status: "pending",
                founderId: founderId,
              }
              : f,
          ),
        )
        toast.success("KYC submitted successfully")
        setKycModalOpen(false)
      } else {
        toast.error("Failed to submit KYC - no founder ID returned")
      }
    },
    onError: (err) => {
      const message = err?.response?.data?.message || "Error submitting KYC"
      toast.error(message)
    }
  })

  const submitKyc = async () => {
    if (!currentFounderForKyc) return
    setIsSubmitting(true)
    try {
      await submitKycMutation.mutateAsync(kycData)
    } finally {
      setIsSubmitting(false)
      setCurrentFounderForKyc(null)
    }
  }

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text)
    toast.success("Copied to clipboard!")
  }

  // Mutation for fetching founder by ID
  const fetchFounderMutation = useMutation({
    mutationFn: async (id) => {
      const response = await getFounderById(id)
      return response.data
    },
    onSuccess: (founder) => {
      setFounderForm({
        name: founder.name,
        email: founder.email,
        phone: founder.phone || "",
        startupId: founder.startupId,
      })
      setEditingFounderId(founder._id)
      setAddFounderModalOpen(true)
    },
    onError: () => {
      toast.error("Failed to fetch founder details")
    }
  })

  const handleEditFounder = async (id) => {
    await fetchFounderMutation.mutateAsync(id)
  }

  // Mutation for adding/updating founder
  const founderMutation = useMutation({
    mutationFn: async () => {
      if (editingFounderId) {
        return await updateFounder(editingFounderId, founderForm)
      } else {
        const founderData = {
          ...founderForm,
          startupId: formData.startupId,
        }
        return await createFounder(founderData)
      }
    },
    onSuccess: (response) => {
      if (editingFounderId) {
        const updatedFounder = response.data
        setFounders(
          founders.map((f) =>
            f.id === editingFounderId
              ? {
                ...updatedFounder,
                id: updatedFounder._id,
                sno: f.sno,
                founderId: f.founderId,
                status: f.status,
              }
              : f,
          ),
        )
        toast.success("Founder updated successfully")
      } else {
        const newFounder = response.data
        setFounders([
          ...founders,
          {
            ...newFounder,
            id: newFounder._id,
            sno: founders.length + 1,
            founderId: "Verify KYC to get Founder ID",
            status: "Not Submitted",
          },
        ])
        toast.success("Founder added successfully")
      }
      setFounderForm({ name: "", email: "", phone: "" })
      setEditingFounderId(null)
      setAddFounderModalOpen(false)
    },
    onError: () => {
      toast.error(editingFounderId ? "Failed to update founder" : "Failed to add founder")
    }
  })

  const handleAddFounder = async () => {
    if (!founderForm.name || !founderForm.email) {
      toast.error("Name and Email are required")
      return
    }
    await founderMutation.mutateAsync()
  }

  // Mutation for deleting founder
  const deleteFounderMutation = useMutation({
    mutationFn: async (id) => {
      return await deleteFounder(id)
    },
    onSuccess: () => {
      setFounders(founders.filter((founder) => founder.id !== founderToDelete))
      toast.success("Founder deleted successfully")
      setDeleteModalOpen(false)
      setFounderToDelete(null)
    },
    onError: () => {
      toast.error("Failed to delete founder")
    }
  })

  const confirmDeleteFounder = async () => {
    if (!founderToDelete) return
    await deleteFounderMutation.mutateAsync(founderToDelete)
  }

  const renderStatusButton = (founder) => {
    switch (founder.status.toLowerCase()) {
      case "approved":
        return (
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200">
              KYC Verified
            </span>
          </div>
        )
      case "pending":
        return (
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-yellow-500" />
            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 border border-yellow-200">
              Pending Review
            </span>
          </div>
        )
      default:
        return (
          <button
            onClick={() => openKycModalForFounder(founder)}
            className="flex items-center gap-2 px-4 py-2 text-xs font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            <AlertCircle className="w-4 h-4" />
            Verify KYC
          </button>
        )
    }
  }

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match('image.*')) {
        toast.error('Please select an image file');
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size should be less than 2MB');
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setLogoFile(file);
      setFormData(prev => ({
        ...prev,
        logo: previewUrl,
        logoUrl: previewUrl
      }));

      // Update progress after logo change
      setTimeout(() => {
        setProgressPercentage(calculateProgress({ ...formData, logoUrl: previewUrl }));
      }, 100);
    }
  };

  const handlePitchDeckUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPitchDeckUploading(true);
    try {
      const { url } = await uploadFile(file);
      setFormData(prev => ({
        ...prev,
        pitchDeck: url,
      }));
      setPitchDeckFile(file);
      toast.success("Pitch deck uploaded successfully!");
    } catch (err) {
      toast.error("Failed to upload pitch deck");
      console.error("Pitch deck upload error:", err);
    } finally {
      setPitchDeckUploading(false);
    }
  };

  const handleKycInputChange = (e) => {
    const { name, value } = e.target;
    setKycData(prev => ({ ...prev, [name]: value }));
  };

  const handleFounderInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const numericValue = value.replace(/\D/g, '').slice(0, 10);

      if (numericValue.length === 10 && !validatePhoneNumber(numericValue)) {
        toast.error("Phone number should not start with 0");
        return;
      }

      setFounderForm(prev => ({ ...prev, [name]: numericValue }));
    } else {
      setFounderForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleDeleteFounder = (id) => {
    setFounderToDelete(id);
    setDeleteModalOpen(true);
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 overflow-hidden">
      <ModernSidebar
        isOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        isMobile={isMobile}
        fullName={fullName}
        email={email}
      />
      <div
        className={`flex flex-col flex-1 transition-all duration-300 ${!isMobile ? (sidebarOpen ? "ml-16 sm:ml-18 md:ml-20 lg:ml-20 xl:ml-20" : "ml-0") : ""}`}
      >
        <ModernHeader toggleSidebar={toggleSidebar} isMobile={isMobile} />

        <div className="flex-1 px-4 sm:px-6 md:px-8 pb-8 overflow-y-auto">
          <div className="relative mb-8">
            <div className="bg-gradient-to-r from-black via-gray-900 to-slate-700 rounded-2xl px-8 py-4 lg:mt-8 shadow-2xl">
              <div className="absolute inset-0 bg-black/10 rounded-2xl"></div>
              <div className="relative flex flex-col md:flex-row items-center gap-6">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border-4 border-white/30 shadow-xl">
                    {formData.logo ? (
                      <img
                        src={formData.logo}
                        alt="Profile"
                        className="w-full h-full rounded-full object-cover"
                        onError={e => {
                          e.target.onerror = null;
                          e.target.src = "/no_image.jpg";
                        }}
                      />
                    ) : (
                      <img
                        src="/no_image.jpg"
                        alt="Profile"
                        className="w-full h-full rounded-full object-cover"
                      />
                    )}
                  </div>
                  <div className="absolute -bottom-2 -right-2 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full p-1 shadow-lg">
                    <img src="/l2.svg" alt="Tactos Logo" className="w-8 h-8 object-cover" />
                  </div>
                </div>

                <div className="text-center md:text-left text-white">
                  <h1 className="text-3xl font-bold mb-2">{formData.fullName}</h1>
                  <p className="text-white/90 mb-1 text-lg">{formData.startupName}</p>
                  <p className="text-white/80 mb-3">{formData.phone}</p>
                  <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                    <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium border border-white/30">
                      ID: {formData.startupId || "-"}
                    </div>
                    <div className={`px-4 py-2 rounded-full text-sm font-medium border ${formData.isRegistered ? "bg-green-500/20 border-green-300 text-green-200" : "bg-yellow-500/20 border-yellow-300 text-yellow-200"}`}>
                      {formData.isRegistered ? "Registered Company" : "Non-registered Company"}
                    </div>
                  </div>
                </div>

                <div className="ml-auto">
                  <button
                    onClick={() =>{
                      if(isGuest()){
                        toast.error("Please login to edit profile!")
                        navigate("/login")
                      }else{
                        setEditMode(!editMode)
                      }
                    }}
                    className="flex items-center gap-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white px-6 py-3 rounded-xl transition-all duration-200 border border-white/30 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  >
                    <Pencil size={18} />
                    {editMode ? "Cancel" : "Edit Profile"}
                  </button>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="relative mt-6">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium text-white">Profile Completion</span>
                  <span className="text-sm font-medium text-white">{progressPercentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-gradient-to-r from-blue-400 to-indigo-600 h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Left Column - Profile Details */}
            <div className="xl:col-span-2 space-y-8">
              {/* Startup Profile Card */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/50">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl">
                    <Building className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800">Startup Profile</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Startup Name */}
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-600 mb-2">
                      Startup Name *
                    </label>
                    {editMode ? (
                      <input
                        name="startupName"
                        value={formData.startupName || ""}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                        required
                      />
                    ) : (
                      <div className="w-full px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200 font-medium text-gray-800">
                        {formData.startupName || "-"}
                      </div>
                    )}
                  </div>

                  {/* Short Description */}
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-600 mb-2">
                      Short Description
                    </label>
                    {editMode ? (
                      <input
                        name="shortDescription"
                        value={formData.shortDescription}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                      />
                    ) : (
                      <div className="w-full px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200 font-medium text-gray-800">
                        {formData.shortDescription || "-"}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-600 mb-2">
                      Email *
                    </label>
                    {editMode ? (
                      <input
                        name="email"
                        value={formData.email || ""}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                        required
                      />
                    ) : (
                      <div className="w-full px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200 font-medium text-gray-800">
                        {formData.email || "-"}
                      </div>
                    )}
                  </div>

                  {/* Other contact/social fields */}
                  {[
                    { key: "phone", label: "Phone *", icon: null },
                    { key: "linkedin", label: "LinkedIn Profile *", icon: Linkedin },
                    { key: "twitter", label: "X Profile", icon: X },
                    { key: "instagram", label: "Instagram Profile", icon: Instagram },
                    { key: "facebook", label: "Facebook Profile", icon: Facebook },
                    { key: "dpiitNumber", label: "DPIIT Number", icon: null },
                  ].map(({ key, label, icon: Icon, fullWidth }) => (
                    <div
                      key={key}
                      className={`space-y-2 ${fullWidth ? "md:col-span-2" : ""
                        }`}
                    >
                      <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                        {Icon && <Icon className="w-4 h-4" />}
                        {label}
                      </label>
                      {editMode ? (
                        <input
                          name={key}
                          value={formData[key] || ""}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                          placeholder={
                            ["linkedin", "twitter", "instagram"].includes(key)
                              ? `https://${key}.com/username`
                              : `Enter ${label.toLowerCase()}`
                          }
                          required={label.includes('*')}
                        />
                      ) : (
                        <div className="w-full px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200 font-medium text-gray-800">
                          {["linkedin", "twitter", "instagram"].includes(key) && formData[key] ? (
                            <a
                              href={
                                formData[key].startsWith("http")
                                  ? formData[key]
                                  : `https://${formData[key]}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline flex items-center gap-2"
                            >
                              <Icon className="w-4 h-4" />
                              {formData[key]}
                            </a>
                          ) : (
                            formData[key] || "-"
                          )}
                        </div>
                      )}
                    </div>


                  ))}
                </div>
              </div>

              {/* Founder Details Card */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/50">
                {/* Founders Management Section - Moved here below Co-Founder */}
                <div className="mt-8">
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl">
                        <Users className="w-6 h-6 text-white" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-800">Founder Details</h2>
                    </div>
                    <button
                        onClick={() => {
    if (isGuest()) {
      toast.error("Please login to add founder!");
      navigate("/login");
    } else {
      setFounderForm({ name: "", email: "", phone: "" });
      setEditingFounderId(null);
      setAddFounderModalOpen(true);
    }
  }}
                      className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-2 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <Plus size={18} />
                      Add Founder
                    </button>
                  </div>

                  <div className="overflow-x-auto rounded-xl border border-gray-200">
                    {isLoadingFounders ? (
                      <div className="flex justify-center items-center py-12">
                        <ClockLoader size={32} color="#6366f1" />
                      </div>
                    ) : (
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                          <tr>
                            {["S.No", "Founder Name", "Founder ID", "Email", "Phone", "DIN", "Status", "Actions"].map((header) => (
                              <th
                                key={header}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {founders.map((founder) => (
                            <tr key={founder.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {founder.sno}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-400 to-pink-500 flex items-center justify-center text-white text-xs font-medium">
                                    {getDisplayName(founder.name, founder.email).charAt(0).toUpperCase()}
                                  </div>
                                  <span className="text-sm font-medium text-gray-900">
                                    {getDisplayName(founder.name, founder.email)}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  {founder.status.toLowerCase() === "approved" ? (
                                    <>
                                      <span className="text-sm font-mono text-gray-900 bg-green-50 px-2 py-1 rounded-lg">
                                        {founder.founderId}
                                      </span>
                                      <button
                                        onClick={() => handleCopy(founder.founderId)}
                                        className="text-gray-400 hover:text-gray-600"
                                        title="Copy Founder ID"
                                      >
                                        <Copy className="h-4 w-4" />
                                      </button>
                                    </>
                                  ) : (
                                    <span className="text-sm text-gray-500 italic">Verify KYC to get Founder ID</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{founder.email}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {founder.phone}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {founder.DIN || "-"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">{renderStatusButton(founder)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleEditFounder(founder.id)}
                                    className="text-blue-600 hover:text-blue-900"
                                    title="Edit"
                                  >
                                    <Edit className="h-5 w-5" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteFounder(founder.id)}
                                    className="text-red-600 hover:text-red-900"
                                    title="Delete"
                                  >
                                    <Trash2 className="h-5 w-5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>

              {/* Startup Details Card */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/50">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-gradient-to-r from-green-500 to-teal-600 rounded-xl">
                    <Briefcase className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800">Startup Details</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 mb-2">
                      {formData.isRegistered ? "Industry *" : "Industry"}
                    </label>
                    {editMode ? (
                      <select
                        name="industry"
                        value={formData.industry || ""}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                        required={formData.isRegistered}
                      >
                        <option value="">Select Industry</option>
                        {industries.map((industry) => (
                          <option key={industry._id} value={industry.industryName}>
                            {industry.industryName}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="w-full px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200 font-medium text-gray-800">
                        {formData.industry || "-"}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-600 mb-2">Stage</label>
                    {editMode ? (
                      <select
                        name="stage"
                        value={formData.stage || ""}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                      >
                        <option value="">Select Stage</option>
                        {stages.map((stage) => (
                          <option key={stage._id} value={stage.stageName}>
                            {stage.stageName}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="w-full px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200 font-medium text-gray-800">
                        {formData.stage || "-"}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {formData.isRegistered ? "Location *" : "Location"}
                    </label>
                    {editMode ? (
                      <select
                        name="location"
                        value={formData.location || ""}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                        required={formData.isRegistered}
                      >
                        <option value="">Select Location</option>
                        {tamilNaduDistricts.map((district, index) => (
                          <option key={index} value={district}>
                            {district}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="w-full px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200 font-medium text-gray-800">
                        {formData.location || "-"}
                      </div>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-600 mb-2">Address</label>
                    {editMode ? (
                      <textarea
                        name="address"
                        value={formData.address || ""}
                        onChange={handleChange}
                        rows={3}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                        placeholder="Enter full address"
                      />
                    ) : (
                      <div className="w-full px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200 font-medium text-gray-800">
                        {formData.address || "-"}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Quick Stats */}
            <div className="space-y-6">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/50">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Quick Stats</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                    <span className="text-sm font-medium text-gray-600">Total Founders</span>
                    <span className="text-2xl font-bold text-blue-600">{founders.length}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                    <span className="text-sm font-medium text-gray-600">KYC Verified</span>
                    <span className="text-2xl font-bold text-green-600">
                      {founders.filter((f) => f.status.toLowerCase() === "approved").length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl border border-yellow-200">
                    <span className="text-sm font-medium text-gray-600">Pending KYC</span>
                    <span className="text-2xl font-bold text-yellow-600">
                      {founders.filter((f) => f.status.toLowerCase() === "pending").length}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/50">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800">Documents & Support</h2>
                </div>

                <div className="space-y-6">
                  {/* Pitch Deck Upload */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 mb-2">Pitch Deck</label>
                    {editMode ? (
                      <div className="space-y-3">
                        <label className="flex items-center gap-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl cursor-pointer transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 w-fit">
                          <Upload className="w-5 h-5" />
                          {pitchDeckUploading ? "Uploading..." : "Choose File"}
                          <input
                            type="file"
                            name="pitchDeck"
                            onChange={handlePitchDeckUpload}
                            className="hidden"
                            accept=".pdf,.ppt,.pptx,.doc,.docx"
                          />
                        </label>
                        {formData.pitchDeckFile && (
                          <div className="flex items-center gap-2 text-sm text-gray-700 bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
                            <FileText className="w-4 h-4 text-blue-600" />
                            {formData.pitchDeckFile}
                          </div>
                        )}
                        {formData.pitchDeck && !formData.pitchDeckFile && (
                          <div className="flex items-center gap-2 text-sm text-gray-700 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
                            <FileText className="w-4 h-4 text-gray-600" />
                            Current: {formData.pitchDeck.split('/').pop()}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-700">
                        {formData.pitchDeck ? (
                          <a
                            href={formData.pitchDeck}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 bg-blue-50 px-4 py-3 rounded-xl border border-blue-200 hover:border-blue-300 transition-all duration-200 w-fit"
                          >
                            <FileText className="w-5 h-5" />
                            {formData.pitchDeck.split('/').pop()}
                          </a>
                        ) : (
                          <div className="px-4 py-3 bg-gray-100 rounded-xl text-gray-500">No pitch deck uploaded</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-600 mb-2">
                      {formData.isRegistered ? "Company Logo *" : "Company Logo"}
                    </label>
                    {editMode ? (
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <div className="w-24 h-24 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden">
                            {logoFile ? (
                              <img
                                src={URL.createObjectURL(logoFile)}
                                alt="New Logo Preview"
                                className="w-full h-full object-cover"
                              />
                            ) : formData.logo ? (
                              <img
                                src={formData.logo}
                                alt="Current Logo"
                                className="w-full h-full object-cover"
                                onError={e => {
                                  e.target.onerror = null;
                                  e.target.src = "/no_image.jpg";
                                }}
                              />
                            ) : (
                              <Upload className="w-8 h-8 text-gray-400" />
                            )}
                          </div>
                        </div>
                        <div className="flex-1">
                          <label className="flex flex-col items-center px-4 py-3 bg-white text-blue-600 rounded-lg border border-blue-300 cursor-pointer hover:bg-blue-50">
                            <span className="text-sm font-medium">
                              {logoFile ? "Change Logo" : "Upload Logo"}
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleLogoChange}
                              className="hidden"
                              required={formData.isRegistered && !formData.logoUrl}
                            />
                          </label>
                          {logoFile && (
                            <p className="mt-2 text-xs text-gray-500">
                              {logoFile.name} ({Math.round(logoFile.size / 1024)} KB)
                            </p>
                          )}
                          {logoFile && (
                            <button
                              onClick={() => {
                                setLogoFile(null);
                                // Reset to original logo if available
                                setFormData(prev => ({
                                  ...prev,
                                  logo: prev.logoUrl
                                }));
                              }}
                              className="mt-2 text-xs text-red-500 hover:text-red-700"
                            >
                              Remove selected logo
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gray-100 border-2 border-gray-200 overflow-hidden">
                        {formData.logo ? (
                          <img
                            src={formData.logo}
                            alt="Company Logo"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <User className="w-8 h-8" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {editMode && (
                  <div className="flex justify-end mt-6">
                    <button
                      onClick={handleSave}
                      disabled={isSubmitting}
                      className="flex items-center gap-2 bg-black hover:from-green-700 hover:to-emerald-700 text-white px-8 py-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 font-semibold"
                    >
                      {isSubmitting ? (
                        <>
                          <ClockLoader size={20} color="#fff" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* KYC Modal */}
          {/* Founder KYC Modal */}
          {kycModalOpen && (
            <Modal isOpen={kycModalOpen} onClose={() => setKycModalOpen(false)}>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">
                  Founder KYC Verification
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Startup Id (read-only) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-2">
                    Startup Id
                  </label>
                  <input
                    type="text"
                    name="startupId"
                    value={kycData.startupId}
                    readOnly
                    className="w-full px-4 py-3 bg-gray-100 border-2 border-gray-200 rounded-xl cursor-not-allowed"
                  />
                </div>

                {/* Other KYC fields, except startupId */}
                {Object.entries(kycData)
                  .filter(([key]) => key !== "startupId")
                  .map(([key, value]) => {
                    const isAddress = key === "address";
                    return (
                      <div key={key} className={isAddress ? "md:col-span-2" : ""}>
                        <label className="block text-sm font-semibold text-gray-600 mb-2 capitalize">
                          {key.replace(/([A-Z])/g, " $1")}
                          {isAddress && (
                            <span className="text-xs text-gray-500 ml-1">
                              (Same as in Aadhar)
                            </span>
                          )}
                        </label>
                        <input
                          type="text"
                          name={key}
                          value={value}
                          onChange={handleKycInputChange}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                          placeholder={`Enter ${key.replace(/([A-Z])/g, " $1").toLowerCase()}`}
                          onKeyDown={(e) => e.stopPropagation()}
                        />
                      </div>
                    );
                  })}
              </div>

              <div className="mt-8 flex justify-end gap-4">
                <button
                  onClick={() => setKycModalOpen(false)}
                  className="px-6 py-3 rounded-xl border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={submitKyc}
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl flex items-center justify-center gap-2 disabled:opacity-60 font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  {isSubmitting ? (
                    <>
                      <ClockLoader size={18} color="#fff" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Submit KYC
                    </>
                  )}
                </button>
              </div>
            </Modal>
          )}


          {/* Add/Edit Founder Modal */}
          {addFounderModalOpen && (
            <Modal isOpen={addFounderModalOpen} onClose={() => {
              setAddFounderModalOpen(false);
              setEditingFounderId(null);
            }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">
                  {editingFounderId ? "Edit Founder" : "Add New Founder"}
                </h3>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-2">Full Name*</label>
                  <input
                    type="text"
                    name="name"
                    value={founderForm.name}
                    onChange={(e) => {
                      setFounderForm(prev => ({
                        ...prev,
                        [e.target.name]: e.target.value
                      }));
                    }}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                    placeholder="Enter full name"
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-2">Email*</label>
                  <input
                    type="email"
                    name="email"
                    value={founderForm.email}
                    onChange={handleFounderInputChange} // Changed to use the improved handler
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                    placeholder="Enter email address"
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={founderForm.phone}
                    onChange={handleFounderInputChange} // Changed to use the improved handler
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                    placeholder="Enter phone number"
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </div>
              </div>

              <div className="mt-8 flex justify-end gap-4">
                <button
                  onClick={() => {
                    setAddFounderModalOpen(false)
                    setEditingFounderId(null)
                  }}
                  className="px-6 py-3 rounded-xl border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddFounder}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl flex items-center gap-2 font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  disabled={isSubmitting}
                >
                  {isSubmitting && <ClockLoader size={18} color="#fff" />}
                  {editingFounderId ? "Update Founder" : "Add Founder"}
                </button>
              </div>
            </Modal>
          )}

          {/* Delete Confirmation Modal */}
          {deleteModalOpen && (
            <Modal isOpen={deleteModalOpen} onClose={() => {
              setDeleteModalOpen(false);
              setFounderToDelete(null);
            }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-gradient-to-r from-red-500 to-pink-600 rounded-xl">
                  <Trash2 className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Confirm Deletion</h3>
              </div>

              <p className="text-gray-600 mb-8 text-lg">
                Are you sure you want to delete this founder? This action cannot be undone and will permanently remove all
                associated data.
              </p>

              <div className="flex justify-end gap-4">
                <button
                  onClick={() => {
                    setDeleteModalOpen(false)
                    setFounderToDelete(null)
                  }}
                  className="px-6 py-3 rounded-xl border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteFounder}
                  className="px-6 py-3 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Delete Founder
                </button>
              </div>
            </Modal>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminProfile;